/**
 * Settings registry (docs/05). One schema drives the settings UI, search, validation,
 * persistence and scope resolution:
 *
 *   default  <  device  <  user  <  preset (scene.settings)  <  live
 *
 * - device: belongs to this machine (audio devices, latency, GPU/perf)
 * - user:   personal preferences (safety, Auto show, controls)
 * - preset: travels with a scene (intensity, beat reaction, smoothing)
 */

export type TargetFps = 'auto' | 30 | 60 | 120 | 144;
export type Scope = 'device' | 'user' | 'preset';
export type Category =
  | 'Audio'
  | 'Beat & analysis'
  | 'Visual'
  | 'Auto show'
  | 'Performance'
  | 'Safety & accessibility'
  | 'Controls'
  | 'Advanced';

export const CATEGORIES: readonly Category[] = [
  'Audio',
  'Beat & analysis',
  'Visual',
  'Auto show',
  'Performance',
  'Safety & accessibility',
  'Controls',
  'Advanced',
];

export interface Settings {
  // Audio
  inputGain: number;
  monitorInput: boolean;
  delayCompensation: boolean;
  syncOffsetMs: number;
  visualLatencyMs: number;
  // Beat & analysis
  beatDetection: boolean;
  beatsPerBar: number;
  bpmMin: number;
  bpmMax: number;
  followMidiClock: boolean;
  phraseBars: number;
  // Visual (preset scope)
  intensity: number;
  beatReact: number;
  smoothing: number;
  milkdropPack: boolean;
  // Auto show (the setting keys keep their auto-pilot names, so saved settings carry over)
  /** Auto show on. */
  autopilot: boolean;
  /** Bars between changes. */
  autopilotBars: number;
  /** React to drops, breakdowns and new sections. */
  autopilotOnSections: boolean;
  autopilotSelection: 'sequential' | 'shuffle' | 'mood';
  /** Where whole new looks come from. */
  autopilotPool: 'generate' | 'bank' | 'all' | 'favorites';
  autopilotHoldPhrases: number;
  autoColours: boolean;
  autoTop: boolean;
  autoBackground: boolean;
  autoEffects: boolean;
  autoWhole: 'never' | 'sections' | 'often';
  autoFollowEnergy: boolean;
  cycleMilkdrop: boolean;
  // Performance
  adaptiveQuality: boolean;
  targetFps: TargetFps;
  scaleMin: number;
  scaleMax: number;
  // Safety & accessibility
  safeMode: boolean;
  reducedMotion: boolean;
  // Controls
  showWelcome: boolean;
  hideControlsAfterMs: number;
  midiEnabled: boolean;
  // Advanced
  forceMainThreadRender: boolean;
}

/** The engine consumes the same resolved settings object. */
export type EngineSettings = Settings;

interface Base<K extends keyof Settings> {
  key: K;
  label: string;
  description: string;
  category: Category;
  scope: Scope;
  advanced?: boolean;
  /** Takes effect only after a reload. */
  reload?: boolean;
  /**
   * On/off setting(s) this one depends on: it only matters (and is only shown) when at least one
   * of them is on.
   */
  parent?: keyof Settings | readonly (keyof Settings)[];
}

export type SettingDef =
  | (Base<keyof Settings> & {
      kind: 'number';
      min: number;
      max: number;
      step: number;
      unit?: string;
    })
  | (Base<keyof Settings> & { kind: 'bool' })
  | (Base<keyof Settings> & {
      kind: 'select';
      options: { value: string | number; label: string }[];
    });

const num = (
  key: keyof Settings,
  label: string,
  category: Category,
  scope: Scope,
  min: number,
  max: number,
  step: number,
  description: string,
  extra: { unit?: string; advanced?: boolean; parent?: Base<keyof Settings>['parent'] } = {},
): SettingDef => ({
  key,
  label,
  category,
  scope,
  kind: 'number',
  min,
  max,
  step,
  description,
  ...extra,
});

const bool = (
  key: keyof Settings,
  label: string,
  category: Category,
  scope: Scope,
  description: string,
  extra: { advanced?: boolean; reload?: boolean; parent?: Base<keyof Settings>['parent'] } = {},
): SettingDef => ({ key, label, category, scope, kind: 'bool', description, ...extra });

export const SETTING_DEFS: readonly SettingDef[] = [
  num(
    'inputGain',
    'Input gain',
    'Audio',
    'device',
    0,
    4,
    0.05,
    'Boost or cut the analysed signal (does not change what you hear).',
  ),
  bool(
    'monitorInput',
    'Play microphone through speakers',
    'Audio',
    'device',
    'Usually off: it can cause feedback.',
  ),
  bool(
    'delayCompensation',
    'Delay playback to match visuals',
    'Audio',
    'device',
    'Delays sound we play (files, demo, captured tabs) so picture and sound land together.',
  ),
  num(
    'syncOffsetMs',
    'Sync offset',
    'Audio',
    'device',
    -250,
    250,
    1,
    'Positive = visuals later. Use the calibration tool to set this.',
    { unit: 'ms' },
  ),
  num(
    'visualLatencyMs',
    'Display latency',
    'Audio',
    'device',
    0,
    150,
    1,
    'Time from rendering to light leaving the screen. TVs without game mode need more.',
    { unit: 'ms' },
  ),

  bool(
    'beatDetection',
    'Beat detection',
    'Beat & analysis',
    'user',
    'Follow the music’s tempo, beat one and time signature. Off: the tempo holds where it is and only changes when you tap, nudge or press Beat 1.',
  ),
  {
    key: 'beatsPerBar',
    label: 'Time signature',
    category: 'Beat & analysis',
    scope: 'user',
    kind: 'select',
    description:
      'Beats per bar, for bar- and phrase-timed changes and the beat dots. Detect tells 3/4 from 4/4 (and holds still while beat detection is off).',
    options: [
      { value: 0, label: 'Detect' },
      { value: 2, label: '2/4' },
      { value: 3, label: '3/4' },
      { value: 4, label: '4/4' },
      { value: 5, label: '5/4' },
      { value: 6, label: '6/8' },
      { value: 7, label: '7/8' },
    ],
  },
  num(
    'bpmMin',
    'Slowest tempo',
    'Beat & analysis',
    'user',
    50,
    140,
    1,
    'Lower bound for tempo detection.',
    { unit: 'BPM' },
  ),
  num(
    'bpmMax',
    'Fastest tempo',
    'Beat & analysis',
    'user',
    100,
    220,
    1,
    'Upper bound for tempo detection.',
    { unit: 'BPM' },
  ),
  bool(
    'followMidiClock',
    'Follow MIDI clock',
    'Beat & analysis',
    'user',
    'When a MIDI clock is received, it overrides beat detection.',
    { parent: 'midiEnabled' },
  ),
  num(
    'phraseBars',
    'Phrase length',
    'Beat & analysis',
    'user',
    4,
    32,
    4,
    'Bars per phrase, for phrase-quantized changes and Auto show.',
    { unit: 'bars' },
  ),

  num(
    'intensity',
    'Intensity',
    'Visual',
    'preset',
    0,
    2,
    0.01,
    'Master brightness/strength. Saved with the preset.',
  ),
  num(
    'beatReact',
    'Beat reaction',
    'Visual',
    'preset',
    0,
    2,
    0.01,
    'How strongly motion follows the beat. Saved with the preset.',
  ),
  num(
    'smoothing',
    'Smoothing',
    'Visual',
    'preset',
    0,
    1,
    0.01,
    '0 = twitchy and immediate, 1 = calm and flowing. Saved with the preset.',
  ),
  bool(
    'milkdropPack',
    'Classic MilkDrop presets',
    'Visual',
    'user',
    'Downloads the open-source Butterchurn community preset pack (about 0.6 MB, from jsDelivr) and adds its presets as visualizations. Presets are made by the MilkDrop community.',
  ),

  bool(
    'autopilot',
    'Auto show',
    'Auto show',
    'user',
    'Run the show automatically: change one thing at a time with the music, and the whole look on the big moments.',
  ),
  num(
    'autopilotBars',
    'Change something every',
    'Auto show',
    'user',
    1,
    64,
    1,
    'Bars between changes (sooner when the music is intense, if Follow the energy is on).',
    { unit: 'bars', parent: 'autopilot' },
  ),
  bool('autoColours', 'Change the colours', 'Auto show', 'user', 'Move to another palette.', {
    parent: 'autopilot',
  }),
  bool(
    'autoTop',
    'Change the top layers',
    'Auto show',
    'user',
    'Swap, add or remove the shapes-and-particles layers over the background.',
    { parent: 'autopilot' },
  ),
  bool(
    'autoBackground',
    'Change the background',
    'Auto show',
    'user',
    'Give the bottom layer another full-screen visualization.',
    { parent: 'autopilot' },
  ),
  bool(
    'autoEffects',
    'Change the effects',
    'Auto show',
    'user',
    'Bring in or change trails, mirrors and kaleidoscopes, or clear them.',
    { parent: 'autopilot' },
  ),
  {
    key: 'autoWhole',
    label: 'Whole new look',
    category: 'Auto show',
    scope: 'user',
    kind: 'select',
    parent: 'autopilot',
    description:
      'When to change everything at once: never, on drops and new sections, or also every few changes.',
    options: [
      { value: 'never', label: 'Never' },
      { value: 'sections', label: 'On drops & new sections' },
      { value: 'often', label: 'Also every few changes' },
    ],
  },
  {
    key: 'autopilotPool',
    label: 'New looks from',
    category: 'Auto show',
    scope: 'user',
    kind: 'select',
    parent: 'autopilot',
    description: 'Where whole new looks come from: made up on the spot, or one of your presets.',
    options: [
      { value: 'generate', label: 'Made up (like Surprise me)' },
      { value: 'bank', label: 'Current bank' },
      { value: 'all', label: 'All presets' },
      { value: 'favorites', label: 'Favorites' },
    ],
  },
  {
    key: 'autopilotSelection',
    label: 'Pick presets',
    category: 'Auto show',
    scope: 'user',
    kind: 'select',
    parent: 'autopilot',
    description: 'How a preset is chosen for a whole new look (when new looks come from presets).',
    options: [
      { value: 'sequential', label: 'In order' },
      { value: 'shuffle', label: 'Shuffle' },
      { value: 'mood', label: 'Match the mood' },
    ],
  },
  bool(
    'autoFollowEnergy',
    'Follow the energy',
    'Auto show',
    'user',
    'Match the visualizations, colours, effects and pace to how intense the music is.',
    { parent: 'autopilot' },
  ),
  bool(
    'autopilotOnSections',
    'React to drops & sections',
    'Auto show',
    'user',
    'A whole new look on a drop, a stripped-back one in a breakdown, effects in a build-up, a new background for a new section.',
    { parent: 'autopilot' },
  ),
  num(
    'autopilotHoldPhrases',
    'Pause after you change something',
    'Auto show',
    'user',
    0,
    8,
    1,
    'Phrases to wait after you change something yourself.',
    { unit: 'phrases', parent: 'autopilot' },
  ),
  bool(
    'cycleMilkdrop',
    'Include MilkDrop presets',
    'Auto show',
    'user',
    'Let Auto show and Surprise me pick MilkDrop presets as backgrounds (when the pack is loaded).',
    { parent: 'milkdropPack' },
  ),

  bool(
    'adaptiveQuality',
    'Adaptive quality',
    'Performance',
    'device',
    'Lower the render resolution (then effects) automatically to hold the target frame rate.',
  ),
  {
    key: 'targetFps',
    label: 'Target frame rate',
    category: 'Performance',
    scope: 'device',
    kind: 'select',
    description: 'Rendering is paced to an even divisor of the display refresh rate.',
    options: [
      { value: 'auto', label: 'Match display' },
      { value: 144, label: '144 fps' },
      { value: 120, label: '120 fps' },
      { value: 60, label: '60 fps' },
      { value: 30, label: '30 fps' },
    ],
  },
  num(
    'scaleMin',
    'Lowest resolution',
    'Performance',
    'device',
    0.25,
    1,
    0.05,
    'Floor for adaptive resolution.',
    { unit: '×', parent: 'adaptiveQuality' },
  ),
  num(
    'scaleMax',
    'Highest resolution',
    'Performance',
    'device',
    0.5,
    2,
    0.05,
    'Ceiling for adaptive resolution (above 1 = supersampling).',
    { unit: '×' },
  ),

  bool(
    'safeMode',
    'Photosensitivity-safe mode',
    'Safety & accessibility',
    'user',
    'Measures on-screen brightness and limits flashes to fewer than 3 per second (WCAG 2.3.1).',
  ),
  bool(
    'reducedMotion',
    'Reduced motion',
    'Safety & accessibility',
    'user',
    'Calmer motion: more smoothing, no beat-cut transitions. Defaults to your system setting.',
  ),

  bool(
    'showWelcome',
    'Welcome when the app opens',
    'Controls',
    'user',
    'Ask which music to play each time you open the app. Off: open straight onto the visuals.',
  ),
  num(
    'hideControlsAfterMs',
    'Hide controls after',
    'Controls',
    'user',
    1000,
    15000,
    500,
    'Idle time before the cursor and controls hide while performing.',
    { unit: 'ms' },
  ),
  bool(
    'midiEnabled',
    'MIDI input',
    'Controls',
    'user',
    'Use MIDI controllers (asks for permission).',
  ),

  bool(
    'forceMainThreadRender',
    'Render on the main thread',
    'Advanced',
    'device',
    'Debugging aid: skip the render worker.',
    { advanced: true, reload: true },
  ),
];

export const DEFAULT_SETTINGS: Settings = {
  inputGain: 1,
  monitorInput: false,
  delayCompensation: true,
  syncOffsetMs: 0,
  visualLatencyMs: 30,
  beatDetection: true,
  beatsPerBar: 0,
  bpmMin: 70,
  bpmMax: 180,
  followMidiClock: true,
  phraseBars: 16,
  intensity: 1,
  beatReact: 1,
  smoothing: 0.5,
  milkdropPack: false,
  autopilot: false,
  autopilotBars: 8,
  autopilotOnSections: true,
  autopilotSelection: 'mood',
  autopilotPool: 'generate',
  autopilotHoldPhrases: 2,
  autoColours: true,
  autoTop: true,
  autoBackground: true,
  autoEffects: true,
  autoWhole: 'sections',
  autoFollowEnergy: true,
  cycleMilkdrop: false,
  adaptiveQuality: true,
  targetFps: 'auto',
  scaleMin: 0.5,
  scaleMax: 1,
  safeMode: true,
  reducedMotion: false,
  showWelcome: true,
  hideControlsAfterMs: 3000,
  midiEnabled: false,
  forceMainThreadRender: false,
};

export const TARGET_FPS_OPTIONS: readonly TargetFps[] = ['auto', 144, 120, 60, 30];

export function findSetting(key: keyof Settings): SettingDef | undefined {
  return SETTING_DEFS.find((d) => d.key === key);
}

/** Validate one value against its definition; returns undefined when invalid. */
export function validateSetting(
  def: SettingDef,
  value: unknown,
): Settings[keyof Settings] | undefined {
  switch (def.kind) {
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? Math.min(def.max, Math.max(def.min, value))
        : undefined;
    case 'bool':
      return typeof value === 'boolean' ? value : undefined;
    case 'select':
      return def.options.some((o) => o.value === value)
        ? (value as Settings[keyof Settings])
        : undefined;
  }
}

/** Keep only valid, known keys of a stored settings layer. */
/**
 * Settings saved before Auto show: old auto-pilot (whole presets every few bars) and auto-cycle
 * (the selected layer's visualization and the palette) become the Auto show that does the same.
 */
export function migrateSettings(input: unknown): unknown {
  if (!input || typeof input !== 'object') return input;
  const raw = input as Record<string, unknown>;
  if ('autoWhole' in raw) return raw;
  const out: Record<string, unknown> = { ...raw };
  if (raw.autopilot === true) {
    Object.assign(out, {
      autoColours: false,
      autoTop: false,
      autoBackground: false,
      autoEffects: false,
      autoWhole: 'often',
      autopilotPool: raw.autopilotPool ?? 'bank',
      autopilotSelection: raw.autopilotSelection ?? 'sequential',
      autopilotBars: raw.autopilotBars ?? 16,
    });
  } else if (raw.cycleViz === true || raw.cyclePalette === true) {
    Object.assign(out, {
      autopilot: true,
      autoColours: raw.cyclePalette === true,
      autoTop: raw.cycleViz === true,
      autoBackground: raw.cycleViz === true,
      autoEffects: false,
      autoWhole: 'never',
      autopilotOnSections: false,
      autoFollowEnergy: false,
      autopilotBars: raw.cycleBars ?? 8,
    });
  }
  return out;
}

export function sanitizeLayer(input: unknown, scope?: Scope): Partial<Settings> {
  const out: Partial<Settings> = {};
  if (!input || typeof input !== 'object') return out;
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    const def = findSetting(k as keyof Settings);
    if (!def || (scope && def.scope !== scope)) continue;
    const val = validateSetting(def, v);
    if (val !== undefined) (out as Record<string, unknown>)[k] = val;
  }
  return out;
}

/** Resolve the layered settings into one object. */
export function resolveSettings(...layers: Partial<Settings>[]): Settings {
  const s = { ...DEFAULT_SETTINGS };
  for (const layer of layers) Object.assign(s, layer);
  if (s.scaleMin > s.scaleMax) s.scaleMin = s.scaleMax;
  if (s.bpmMin > s.bpmMax - 20) s.bpmMin = Math.max(50, s.bpmMax - 20);
  return s;
}

/** Back-compat for v1 single-blob storage and old call sites. */
export function sanitizeSettings(input: unknown): Settings {
  return resolveSettings(sanitizeLayer(input));
}

/** Simple search over label, description, category and key. */
export function searchSettings(query: string): SettingDef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...SETTING_DEFS];
  return SETTING_DEFS.filter((d) =>
    `${d.label} ${d.description} ${d.category} ${d.key}`.toLowerCase().includes(q),
  );
}

/** Parent settings of a setting (empty when it stands alone). */
export function settingParents(def: Pick<SettingDef, 'parent'>): (keyof Settings)[] {
  return def.parent === undefined
    ? []
    : Array.isArray(def.parent)
      ? [...def.parent]
      : [def.parent as keyof Settings];
}

/** A dependent setting is shown only while at least one of its parents is on. */
export function settingVisible(def: Pick<SettingDef, 'parent'>, settings: Settings): boolean {
  const parents = settingParents(def);
  return parents.length === 0 || parents.some((k) => settings[k] === true);
}
